// HeatmapLegend.jsx
// Displays a colorbar legend for the heatmap using the current colormap and opacity

import React from "react";
import PropTypes from "prop-types";
import { getColorMapColor } from "../../viewer/utils/Constants";

/**
 * Heatmap legend rendered on a small canvas with a checkerboard background to show transparency.
 *
 * Props:
 * - colormapType: string name of the colormap (e.g., "jet", "viridis")
 * - opacity: number in [0,1] applied to the legend gradient to reflect current setting
 * - width: px width of the legend bar
 * - height: px height of the legend bar
 * - style: additional style overrides for the outer container
 */
export default function HeatmapLegend({
  colormapType = "viridis",
  opacity = 1,
  width = 220,
  height = 14,
  style = {},
  variant = "overlay", // 'overlay' | 'inline'
}) {
  const canvasRef = React.useRef(null);
  const barRef = React.useRef(null);
  const [measuredWidth, setMeasuredWidth] = React.useState(0);

  // Track parent/container width so the legend can fill available space responsively
  React.useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const update = () => setMeasuredWidth(el.clientWidth || 0);
    update();

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener("resize", update);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", update);
    };
  }, []);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const numericWidth = typeof width === "number" ? width : null;
    const effectiveWidth = numericWidth ?? measuredWidth;
    const w = Math.max(1, Math.floor(effectiveWidth || 0));
    const h = Math.max(1, Math.floor(height));
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    // Draw gradient by sampling the colormap across the width
    for (let x = 0; x < w; x++) {
      const t = x / (w - 1);
      ctx.fillStyle = getColorMapColor(colormapType, t, opacity);
      ctx.fillRect(x, 0, 1, h);
    }
  }, [colormapType, opacity, width, height, measuredWidth]);

  const isOverlay = variant === "overlay";
  const containerStyle = {
    position: isOverlay ? "absolute" : "static",
    right: isOverlay ? 10 : undefined,
    bottom: isOverlay ? 10 : undefined,
    zIndex: isOverlay ? 9999 : "auto",
    pointerEvents: "none", // avoid blocking interactions
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    width: "100%",
    ...style,
  };

  const barWrapperStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: isOverlay ? 8 : 0,
    background: isOverlay ? "rgba(0,0,0,0.35)" : "transparent",
    borderRadius: isOverlay ? 6 : 0,
    boxShadow: isOverlay ? "0 1px 3px rgba(0,0,0,0.4)" : "none",
    backdropFilter: isOverlay ? "blur(2px)" : "none",
  };

  const checkerStyle = {
    position: "relative",
    width: typeof width === "number" ? width : "100%",
    height,
    borderRadius: 2,
    overflow: "hidden",
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.3)",
    backgroundImage:
      "linear-gradient(45deg, #ccc 25%, transparent 25%)," +
      "linear-gradient(-45deg, #ccc 25%, transparent 25%)," +
      "linear-gradient(45deg, transparent 75%, #ccc 75%)," +
      "linear-gradient(-45deg, transparent 75%, #ccc 75%)",
    backgroundSize: "12px 12px",
    backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0px",
  };

  const labelStyle = {
    fontSize: 11,
    opacity: 0.9,
    lineHeight: 1,
  };

  return (
    <div style={containerStyle}>
      <div style={barWrapperStyle}>
        <span style={labelStyle}>Low</span>
        <div style={checkerStyle} ref={barRef}>
          <canvas ref={canvasRef} />
        </div>
        <span style={labelStyle}>High</span>
      </div>
    </div>
  );
}

HeatmapLegend.propTypes = {
  colormapType: PropTypes.string,
  opacity: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  style: PropTypes.object,
  variant: PropTypes.oneOf(["overlay", "inline"]),
};
